/**
 * Sprint C — SaaS entitlement React hook.
 *
 * Central client-side source of truth for whether the current tenant is
 * write-entitled. Everything else (banners, disabled CTAs, guards) reads
 * from here.
 *
 * Data flow:
 *   GET /api/billing/subscription        (auth required — Firebase ID token)
 *   → { subscription, entitlement }
 *   The `entitlement` object is authoritative from the backend resolver.
 *   We never grant `canWrite` from a Checkout redirect alone.
 *
 * Reload triggers:
 *   • Mount
 *   • Auth user change (via subscription to `useAuth().user`)
 *   • Global 402 `billing_required` responses (see api-error listener at
 *     the bottom of this module — refetches so the banner reflects reality)
 *   • Explicit `refetch()` from callers
 *
 * NOTE: Sprint B introduced /api/billing/subscription — Stage 1 does NOT
 * change that surface. Sprint C only augments the client's read of it.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { getSubscription } from '../services/saasBillingService';

const CACHE_MS = 30 * 1000; // 30s stale-while-revalidate

let _cache = { value: null, at: 0 };
const _listeners = new Set();

function _notify() {
  _listeners.forEach((cb) => {
    try { cb(_cache.value); } catch (_) { /* listener errors are non-fatal */ }
  });
}

async function _fetch({ force = false } = {}) {
  if (!force && _cache.value && (Date.now() - _cache.at) < CACHE_MS) {
    return _cache.value;
  }
  try {
    const data = await getSubscription();
    _cache = { value: data, at: Date.now() };
    _notify();
    return data;
  } catch (e) {
    // Do not clobber last-known-good on transient failures.
    // But if we've never fetched, expose the error so hook consumers see it.
    if (!_cache.value) {
      _cache = { value: { error: e.message || 'Failed to load entitlement' }, at: Date.now() };
      _notify();
    }
    return _cache.value;
  }
}

/**
 * Global 402 handler. When the backend emits `billing_required`, we
 * refetch to sync the banner with the actual entitlement.
 * Wired by importing this module from App.js. Idempotent.
 */
const _originalFetch = window.fetch?.bind(window);
if (_originalFetch && !window.__saasEntitlementFetchPatched) {
  window.__saasEntitlementFetchPatched = true;
  window.fetch = async function patchedFetch(...args) {
    const res = await _originalFetch(...args);
    // Only trigger on our own API surface, not third-party URLs.
    try {
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
      if (res.status === 402 && url.includes('/api/')) {
        // Fire-and-forget; do not block the caller.
        _fetch({ force: true });
      }
    } catch (_) { /* URL parse errors are non-fatal */ }
    return res;
  };
}

/**
 * useEntitlement — subscribe to the current tenant's write entitlement.
 *
 * Returns an object with the resolved fields plus helpers. Always safe to
 * call; before the first fetch resolves, returns null-ish defaults with
 * `loading: true`.
 */
export function useEntitlement() {
  const [state, setState] = useState(_cache.value);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    const listener = (v) => { if (mounted.current) setState(v); };
    _listeners.add(listener);
    // Kick off a fetch on mount (respects cache).
    _fetch();
    return () => {
      mounted.current = false;
      _listeners.delete(listener);
    };
  }, []);

  const refetch = useCallback(() => _fetch({ force: true }), []);

  const ent  = state?.entitlement || null;
  const sub  = state?.subscription || null;
  const loading = state === null;

  return {
    loading,
    subscription: sub,
    entitlement:  ent,
    canWrite:     ent?.canWrite ?? false,
    entitled:     ent?.entitled ?? false,
    readOnly:     ent?.readOnly ?? false,
    reason:       ent?.reason  ?? null,
    status:       ent?.status  ?? null,
    refetch,
  };
}

/**
 * Fire-and-forget helper for imperative code (outside React) that wants
 * to trigger an entitlement refresh, e.g., after a billing action.
 */
export function refreshEntitlementCache() {
  return _fetch({ force: true });
}
